import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import ReservaDetail from './ReservaDetail';
import './MisReservas.css';

const MisReservas = () => {
    const { user } = useContext(AuthContext);
    const [reservas, setReservas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedReserva, setSelectedReserva] = useState(null);

    useEffect(() => {
        if (user && user.email) {
            fetchReservas();
        }
    }, [user]);

    const fetchReservas = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`/api/mis-reservas/${encodeURIComponent(user.email)}`);
            const data = await response.json();
            
            if (data.success) {
                setReservas(data.data);
            } else {
                setError(data.message || 'Error al cargar las reservas');
            }
        } catch (err) {
            console.error('Error al obtener reservas:', err);
            setError('Error de conexión al servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleVerDetalle = (reserva) => {
        setSelectedReserva(reserva);
    };

    const handleCloseDetail = () => {
        setSelectedReserva(null);
    };

    const handleEvaluacionActualizada = () => {
        // Recargar las reservas para mostrar la evaluación actualizada
        fetchReservas();
    };

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatearPrecio = (precio) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(precio);
    };

    const getEstadoColor = (estado) => {
        switch (estado?.toLowerCase()) {
            case 'confirmada':
                return 'estado-confirmada';
            case 'pendiente':
                return 'estado-pendiente';
            case 'cancelada':
                return 'estado-cancelada';
            case 'completada':
                return 'estado-completada';
            default:
                return 'estado-default';
        }
    };

    const renderEstrellas = (evaluacion) => {
        if (!evaluacion) return null;
        
        return (
            <div className="estrellas-evaluacion">
                {[1, 2, 3, 4, 5].map(star => (
                    <span 
                        key={star} 
                        className={`estrella ${star <= evaluacion ? 'activa' : ''}`}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    if (!user) {
        return (
            <div className="mis-reservas-container">
                <div className="no-auth-message">
                    <h2>Acceso Restringido</h2>
                    <p>Debes iniciar sesión para ver tus reservas.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="mis-reservas-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando tus reservas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mis-reservas-container">
                <div className="error-container">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={fetchReservas} className="btn-retry">
                        Intentar de nuevo
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mis-reservas-container">
            <div className="mis-reservas-header">
                <h1>Mis Reservas</h1>
                <p>Aquí puedes ver todas tus reservas y evaluarlas</p>
            </div>

            {reservas.length === 0 ? (
                <div className="no-reservas">
                    <div className="no-reservas-icon">📅</div>
                    <h2>No tienes reservas</h2>
                    <p>Cuando realices una reserva, aparecerá aquí.</p>
                </div>
            ) : (
                <div className="reservas-grid">
                    {reservas.map((reserva) => (
                        <div key={reserva.id} className="reserva-card">
                            <div className="reserva-header">
                                <h3 className="habitacion-titulo">{reserva.habitacion_titulo}</h3>
                                <span className={`estado-badge ${getEstadoColor(reserva.estado_reserva)}`}>
                                    {reserva.estado_reserva}
                                </span>
                            </div>
                            
                            <div className="reserva-info">
                                <div className="info-row">
                                    <span className="info-label">📍 Dirección:</span>
                                    <span className="info-value">{reserva.habitacion_direccion}</span>
                                </div>
                                
                                <div className="info-row">
                                    <span className="info-label">📅 Check-in:</span>
                                    <span className="info-value">{formatearFecha(reserva.fecha_checkin)}</span>
                                </div>
                                
                                <div className="info-row">
                                    <span className="info-label">📅 Check-out:</span>
                                    <span className="info-value">{formatearFecha(reserva.fecha_checkout)}</span>
                                </div>
                                
                                <div className="info-row">
                                    <span className="info-label">👥 Huéspedes:</span>
                                    <span className="info-value">{reserva.numero_huespedes}</span>
                                </div>
                                
                                <div className="info-row">
                                    <span className="info-label">💰 Total:</span>
                                    <span className="info-value precio">{formatearPrecio(reserva.precio_total)}</span>
                                </div>
                                
                                <div className="info-row">
                                    <span className="info-label">🏠 Duración:</span>
                                    <span className="info-value">{reserva.duracion_dias} día{reserva.duracion_dias !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                            
                            {reserva.evaluacion && (
                                <div className="evaluacion-existente">
                                    <span className="evaluacion-label">Tu evaluación:</span>
                                    {renderEstrellas(reserva.evaluacion)}
                                    <span className="evaluacion-fecha">
                                        {formatearFecha(reserva.fecha_evaluacion)}
                                    </span>
                                </div>
                            )}
                            
                            {reserva.puede_evaluar && (
                                <div className="puede-evaluar">
                                    <span className="evaluar-badge">✨ Puedes evaluar esta reserva</span>
                                </div>
                            )}
                            
                            <div className="reserva-actions">
                                <button 
                                    onClick={() => handleVerDetalle(reserva)}
                                    className="btn-detalle"
                                >
                                    Ver Detalle
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de detalle de reserva */}
            {selectedReserva && (
                <ReservaDetail
                    reserva={selectedReserva}
                    onClose={handleCloseDetail}
                    onEvaluacionActualizada={handleEvaluacionActualizada}
                />
            )}
        </div>
    );
};

export default MisReservas;